import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, Heart, ClipboardCheck, ArrowRight, ShieldCheck, Phone } from "lucide-react";
import { trackPhoneClick } from "../lib/tracking";

// Three audiences — built as editorial gradient tiles (no stock photos).
// Each one carries warm, benefit-led copy, a premium icon system, and an
// accessible primary CTA + a soft secondary action.
const AUDIENCES = [
  {
    id: "participants",
    overline: "For participants",
    title: "A home that's actually yours.",
    body: "Person-centred SIL & SDA, real Alpha homes, named coordinator. We start where you are — never where a roster says.",
    points: ["Premium SIL with 1:1 / 1:2 only", "Named coordinator who knows you", "Calm, low-stim home environments"],
    Icon: Heart,
    primary: { to: "/for-participants", label: "I'm a participant" },
    secondary: { to: "/easy-read", label: "View Easy Read" },
    tone: "purple",
    accent: "#8659C7",
  },
  {
    id: "families",
    overline: "For families",
    title: "Reassurance, not red tape.",
    body: "Plain-English updates, real photos of real homes, and one number that connects you to a real person — not a queue.",
    points: ["One business day intake response", "Worker-screened, trauma-informed", "24/7 on-call · transparent care"],
    Icon: Users,
    primary: { to: "/for-families", label: "I'm a family member" },
    secondary: { to: "tel:1300380692", label: "Call 1300 380 692", phone: true },
    tone: "lilac",
    accent: "#C8B4F0",
  },
  {
    id: "coordinators",
    overline: "For support coordinators",
    title: "An intake partner you can trust.",
    body: "Live SIL/SDA visibility, plain referral forms, and a same-day reply. We make the warm hand-off, you stay in the loop.",
    points: ["Real-time vacancy visibility", "Streamlined referral channel", "Practice-Standards aligned"],
    Icon: ClipboardCheck,
    primary: { to: "/for-support-coordinators", label: "I'm a coordinator" },
    secondary: { to: "/referrals/coordinator", label: "Send a referral" },
    tone: "green",
    accent: "#4FA56E",
  },
];

function AudienceTile({ a, idx }) {
  const Icon = a.Icon;
  const handlePhone = (e) => {
    if (a.secondary.phone) {
      trackPhoneClick("1300 380 692", `audience-${a.id}`);
    }
  };
  return (
    <article
      className={`group relative overflow-hidden rounded-[24px] md:rounded-[28px] alpha-curtain alpha-audience-tile alpha-audience-${a.tone}`}
      style={{ transitionDelay: `${idx * 80}ms` }}
      data-testid={`audience-tile-${a.id}`}
    >
      {/* Editorial dark base */}
      <div className="absolute inset-0 bg-[#0F0625]" aria-hidden />
      {/* Mesh + grain */}
      <div className="alpha-mesh alpha-mesh-dark" aria-hidden style={{ opacity: 0.55 }} />
      <div className="alpha-grain-dark" aria-hidden />
      {/* Tone-specific accent glow */}
      <div
        className="absolute -top-24 -right-16 w-[320px] h-[320px] rounded-full pointer-events-none transition-transform duration-[1200ms] group-hover:translate-x-2 group-hover:-translate-y-2"
        style={{
          background: `radial-gradient(closest-side, ${a.accent}66, transparent 70%)`,
          filter: "blur(40px)",
        }}
        aria-hidden
      />
      <div
        className="absolute -bottom-24 -left-16 w-[260px] h-[260px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(closest-side, ${a.accent}33, transparent 70%)`,
          filter: "blur(40px)",
        }}
        aria-hidden
      />

      {/* Body */}
      <div className="relative p-6 md:p-8 flex flex-col gap-5 min-h-[460px] md:min-h-[500px] text-white">
        {/* Icon + overline */}
        <div className="flex items-center gap-3">
          <span
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 backdrop-blur-sm border border-white/15"
            style={{ background: `${a.accent}26` }}
            aria-hidden
          >
            <Icon className="w-5 h-5 text-white" />
          </span>
          <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-white/70">
            {a.overline}
          </div>
        </div>

        {/* Headline + body */}
        <div className="flex-1">
          <h3
            className="font-display font-medium tracking-tight leading-[1.05] !text-white"
            style={{ fontSize: "clamp(24px, 2.4vw, 32px)" }}
          >
            {a.title}
          </h3>
          <p className="mt-3 text-white/80 text-[14.5px] leading-relaxed">
            {a.body}
          </p>

          {/* Bullet points */}
          <ul className="mt-5 space-y-2">
            {a.points.map((pt) => (
              <li key={pt} className="flex items-start gap-2.5 text-[13.5px] text-white/85">
                <span
                  className="w-4 h-4 rounded-full mt-[3px] shrink-0 flex items-center justify-center"
                  style={{ background: `${a.accent}40` }}
                  aria-hidden
                >
                  <ShieldCheck className="w-2.5 h-2.5 text-white" />
                </span>
                {pt}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-white/15 flex flex-wrap items-center gap-2.5">
          <Link
            to={a.primary.to}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold transition-transform hover:scale-[1.02]"
            style={{ background: "#fff", color: "#2F1A5C" }}
            data-testid={`audience-${a.id}-primary`}
          >
            {a.primary.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          {a.secondary.phone ? (
            <a
              href={a.secondary.to}
              onClick={handlePhone}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[12.5px] font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors"
              data-testid={`audience-${a.id}-secondary`}
            >
              <Phone className="w-3.5 h-3.5" /> {a.secondary.label}
            </a>
          ) : (
            <Link
              to={a.secondary.to}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[12.5px] font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors"
              data-testid={`audience-${a.id}-secondary`}
            >
              {a.secondary.label}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export default function AudienceCards() {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-audience-cards"
    >
      {/* Soft surface backdrop */}
      <div className="alpha-section-tight relative">
        <div className="alpha-container">
          {/* Editorial header */}
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-7 md:mb-10 alpha-reveal">
            <div className="lg:col-span-7">
              <div className="alpha-overline inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Built for everyone in the plan
              </div>
              <h2 className="mt-3 font-display font-medium tracking-tight leading-[1.04] text-3xl sm:text-[44px] md:text-[52px]">
                Three audiences. <span className="alpha-serif-italic alpha-gradient-text">One promise</span>.
              </h2>
              <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] leading-relaxed max-w-xl">
                Whoever you are in the conversation, Alpha treats you as a partner — clear, calm and quick to respond.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[color:var(--alpha-mute)]">
                Participants · Families · Coordinators
              </div>
            </div>
          </div>

          {/* Three editorial tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
            {AUDIENCES.map((a, i) => (
              <AudienceTile key={a.id} a={a} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
